import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PlayCircle } from "lucide-react";
import { CrewRanking } from "../../../components/os/CrewRanking";
import { api, formatApiError } from "../../../lib/api";

const STATUS_META = {
  planificada: ["Planificada", "text-navy/50"],
  en_progreso: ["En progreso", "text-amber-600"],
  completada: ["Completada", "text-emerald-600"],
};

export default function ExecutionTab({ plan, onChange }) {
  const [run, setRun] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get(`/planning/plans/${plan.id}/schedule`).then((r) => setRun(r.data)).catch(() => {}).finally(() => setLoaded(true));
    api.get(`/planning/plans/${plan.id}/cuadrillas-performance`).then((r) => setPerf(r.data)).catch(() => {});
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);

  const update = async (it, status) => {
    try {
      await api.post(`/planning/plans/${plan.id}/execution/update`, {
        wu_id: it.wu_id, activity: it.activity, status,
        actual_start: status === "en_progreso" ? new Date().toISOString().slice(0, 10) : "",
      });
      toast.success(status === "completada" ? "Actividad completada" : "Actividad iniciada");
      load(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const items = useMemo(() => {
    if (!run) return [];
    let list = [...run.schedule].sort((a, b) => a.start - b.start);
    if (statusFilter) list = list.filter((i) => (i.status || "planificada") === statusFilter);
    if (q) list = list.filter((i) => i.wu_label.toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [run, statusFilter, q]);

  if (!loaded) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy/30" /></div>;
  if (!run) return <div className="rounded-xl border border-dashed border-navy/15 bg-white p-14 text-center text-sm text-navy/40">Genera el plan primero; después inicia la ejecución para registrar avances.</div>;

  const done = run.schedule.filter((i) => i.status === "completada").length;

  return (
    <div className="space-y-3" data-testid="execution-tab">
      <CrewRanking ranking={perf?.ranking} today={perf?.today} testid="plan-crew-ranking" />
      <div className="flex flex-wrap items-center gap-2">
        <input className="bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet w-44"
          placeholder="Filtrar unidad…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="exec-filter" />
        <select className="bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)} data-testid="exec-status-filter">
          <option value="">Todos los estados</option>
          <option value="planificada">Planificadas</option>
          <option value="en_progreso">En progreso</option>
          <option value="completada">Completadas</option>
        </select>
        <span className="ml-auto text-xs text-navy/50">{done} / {run.schedule.length} actividades completadas</span>
      </div>

      <div className="rounded-xl border border-navy/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-navy/10 text-left text-xs text-navy/40">
              {[plan.work_unit_name, "Actividad", "Recurso", "Plan (días)", "Fechas plan", "Real", "Estado", ""].map((h, i) => (
                <th key={i} className="px-3 py-2.5 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 400).map((it, i) => {
              const st = it.status || "planificada";
              const [label, cls] = STATUS_META[st] || [st, ""];
              return (
                <tr key={`${it.wu_id}-${it.activity}-${i}`} className="border-b border-navy/5 hover:bg-navy/[0.02]" data-testid={`exec-row-${it.wu_label}-${it.activity}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{it.wu_label}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{it.activity_name}{it.critical && <span className="text-red-500 ml-1" title="Ruta crítica">●</span>}</td>
                  <td className="px-3 py-2 text-navy/60 whitespace-nowrap">{it.resource}</td>
                  <td className="px-3 py-2 text-navy/50">{it.start_day}–{it.end_day}</td>
                  <td className="px-3 py-2 text-navy/50 text-xs whitespace-nowrap">{it.start_date} → {it.end_date}</td>
                  <td className="px-3 py-2 text-navy/50 text-xs whitespace-nowrap">{it.actual_start || "—"}{it.actual_finish ? ` → ${it.actual_finish}` : ""}</td>
                  <td className={`px-3 py-2 text-xs whitespace-nowrap ${cls}`}>{label}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5">
                      {st === "planificada" && (
                        <button onClick={() => update(it, "en_progreso")} title="Iniciar" data-testid={`exec-start-${it.wu_label}-${it.activity}`}
                          className="text-navy/30 hover:text-amber-600"><PlayCircle className="w-4.5 h-4.5 w-[18px] h-[18px]" /></button>
                      )}
                      {st !== "completada" && (
                        <button onClick={() => update(it, "completada")} title="Completar" data-testid={`exec-complete-${it.wu_label}-${it.activity}`}
                          className="text-navy/30 hover:text-emerald-600"><CheckCircle2 className="w-[18px] h-[18px]" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length > 400 && <p className="px-4 py-2 text-xs text-navy/40">Mostrando 400 de {items.length}: usa los filtros.</p>}
      </div>
    </div>
  );
}
